import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api';
import toast from 'react-hot-toast';

export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem("user");
    return stored ? JSON.parse(stored) : null;
  });
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const location = useLocation();
  const navigate = useNavigate();

  // Check authentication status on mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  useEffect(() => {
    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
    } else {
      localStorage.removeItem("user");
    }
  }, [user]);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/api/auth/status');
      console.log('Auth status check response:', response.data);
      if (response.data.authenticated) {
        setUser(response.data.user);
        const existingToken = localStorage.getItem('token');
        console.log('Session is authenticated, existing token in localStorage:', existingToken ? 'PRESENT' : 'MISSING');
        setToken(existingToken);
      }
    } catch (error) {
      console.error('Auth status check failed (exception):', error);
      // Only clear if it's a real auth failure
      if (error.response && error.response.status === 401) {
        console.warn('Session expired or invalid during status check');
        if (localStorage.getItem('token')) {
          toast.error('Session expired. Please log in again.');
        }
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyToken = async (token, action) => {
      try {
        setLoading(true);
        const response = await api.post('/api/auth/verify', { token, action });
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
          setToken(response.data.token);
          setUser(response.data.user);
          toast.success('Welcome back!');
          navigate('/dashboard');
        }
      } catch (error) {
        console.error('Token verification failed:', error);
        toast.error(error.response?.data?.error || 'Authentication failed');
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };

  const login = async (email) => {
    try {
      setLoading(true);
      const response = await api.post('/api/auth/login', { email });
      toast.success(response.data.message);
      return response.data;
    } catch (error) {
      console.error('Login failed:', error);
      toast.error(error.response?.data?.error || 'Login failed');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await api.post('/api/auth/logout', {});
    } catch (error) {
      console.error('Logout request failed:', error);
    } finally {
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
      toast.success('Logged out successfully');
      navigate('/login');
    }
  };

  const setAuthData = (userData, userToken) => {
    console.log('setAuthData called with:', { user: userData?.email, token: userToken ? 'PRESENT' : 'MISSING' });
    if (userData) {
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
    }
    if (userToken) {
      setToken(userToken);
      localStorage.setItem('token', userToken);
      console.log('Token successfully saved to localStorage');
    } else {
      console.warn('setAuthData called WITHOUT token');
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const value = {
    user,
    loading,
    login,
    logout,
    updateUser,
    setAuthData,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 